<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    offset-y
    v-bind="menuProps"
    min-width="auto"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        v-on="on"
        slot="activator"
        class="v-date-range__input-field"
        :value="inputValue"
        readonly
        :disabled="disabled"
        :placeholder="placeholder"
        label="Date Range"
        v-bind="inputProps"
      ></v-text-field>
    </template>

    <v-card>
      <v-card-text>
        <v-row>
          <v-col v-if="!showPicker">
            <v-list v-if="!noPresets" dense>
              <v-list-item
                v-for="(preset, index) in presets"
                v-model="isPresetActive[index]"
                :key="index"
                @click="selectPreset(index)"
              >
                <v-list-item-content>
                  {{ preset.label }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col v-else>
            <v-date-picker
              v-model="dateStart"
              :locale="locale"
              :first-day-of-week="firstDayOfWeek"
              :min="min"
              :max="dateEnd || max"
              :no-title="noTitle"
              :next-icon="nextIcon"
              :prev-icon="prevIcon"
            ></v-date-picker>
            <v-date-picker
              v-model="dateEnd"
              :locale="locale"
              :first-day-of-week="firstDayOfWeek"
              :min="dateStart || min"
              :max="max"
              :no-title="noTitle"
              :next-icon="nextIcon"
              :prev-icon="prevIcon"
            ></v-date-picker>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions v-show="showPicker">
        <v-spacer></v-spacer>
        <v-btn text @click="reset">{{ mergedActionLabels.reset }}</v-btn>
        <v-btn text @click="menu = false">{{
          mergedActionLabels.cancel
        }}</v-btn>
        <v-btn @click="applyCustomRange" color="info" :disabled="!bothSelected">{{
          mergedActionLabels.apply
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>
<script>
import moment from "moment-timezone";

const ISO_FORMAT = "MM-DD-yyyy";
const DEFAULT_DATE = moment(new Date, ISO_FORMAT).format(ISO_FORMAT);
const DEFAULT_ACTION_LABELS = {
  apply: "Apply",
  cancel: "Cancel",
  reset: "Reset",
};

const nowDate = (value = new Date) => {
  return moment(value, "YYYY-MM-DD");
};

export default {
  name: "v-daterange",
  props: {
    // Take start and end as the input. Passable via v-model.
    value: {
      type: Object,
      default: () => {
        return { start: DEFAULT_DATE, end: DEFAULT_DATE };
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    presets: {
      type: Array,
      default: () => {
        return [
          {
            label: "All time",
            range: [
              null,
              null
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "Today",
            range: [
              nowDate(),
              nowDate()
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "Yesterday",
            range: [
              nowDate().subtract(1, 'day'),
              nowDate()
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "Last 7 Days",
            range: [
              nowDate().subtract(7, 'day'),
              nowDate()
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "Last 30 Days",
            range: [
              nowDate().subtract(30, 'day'),
              nowDate()
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "This Month",
            range: [
              nowDate().startOf('month'),
              nowDate()
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "Last Month",
            range: [
              nowDate().subtract(1, 'month').startOf('month'),
              nowDate().subtract(1, 'month').endOf('month')
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "This Year",
            range: [
              nowDate().startOf('year'),
              nowDate()
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "Last Year",
            range: [
              nowDate().subtract(1, 'year').startOf('year'),
              nowDate().subtract(1, 'year').endOf('year')
            ],
            autoApply: true,
            showPicker: false,
          },
          {
            label: "Custom Range",
            range: [
              nowDate().startOf('month'),
              nowDate()
            ],
            autoApply: false,
            showPicker: true,
          },
        ];
      },
    },
    noPresets: {
      type: Boolean,
      default: false,
    },
    // Denotes the Placeholder string for start date.
    startLabel: {
      type: String,
      default: "Start Date",
    },
    // Denotes the Placeholder string for start date.
    endLabel: {
      type: String,
      default: "End Date",
    },
    // The string that gets placed between `startLabel` and `endLabel`
    separatorLabel: {
      type: String,
      default: "To",
    },
    presetLabel: {
      type: String,
      default: "Presets",
    },
    actionLabels: {
      type: Object,
      default: () => {
        return DEFAULT_ACTION_LABELS;
      },
    },
    /**
     * Following values are all passable to vuetify's own datepicker
     * component.
     */
    // Min selectable date.
    min: {
      type: String,
      default: undefined,
    },
    // Max selectable date
    max: {
      type: String,
      default: undefined,
    },
    // Locale
    locale: {
      type: String,
      default: "en-us",
    },
    firstDayOfWeek: {
      type: [String, Number],
      default: 0,
    },
    noTitle: {
      type: Boolean,
      default: true,
    },
    displayFormat: {
      type: String,
    },
    highlightColor: {
      type: String,
      default: "blue lighten-5",
    },
    showReset: {
      type: Boolean,
      default: true,
    },
    /**
     * Icons
     */
    nextIcon: {
      type: String,
      default: "$vuetify.icons.next",
    },
    prevIcon: {
      type: String,
      default: "$vuetify.icons.prev",
    },
    inputProps: {
      type: Object,
      default: () => {
        return {};
      },
    },
    menuProps: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      menu: false,
      pickerStart: this.value.start,
      pickerEnd: this.value.end,

      dateStart: this.value.start,
      dateEnd: this.value.end,

      highlightDates: [],
      highlightClasses: {},
      showPicker: false,
    };
  },
  computed: {
    inputValue() {
      if (this.isValueEmpty) {
        return "";
      }
      const start = this.value.start ? moment(this.value.start, "YYYY-MM-DD").format(ISO_FORMAT) : "";
      const end = this.value.end  ? moment(this.value.end, "YYYY-MM-DD").format(ISO_FORMAT) : "";
      return `${start}    ${this.separatorLabel}     ${end}`;
    },
    placeholder() {
      return `${this.startLabel}    ${this.separatorLabel}    ${this.endLabel}`;
    },
    /**
     * If the value prop doesn't have any start value,
     * its most likely that an empty object was passed.
     */
    isValueEmpty() {
      return !this.value.start;
    },
    /**
     * If the user has selected both the dates or not
     */
    bothSelected() {
      return this.pickerStart && this.pickerEnd;
    },
    isPresetActive() {
      return this.presets.map(
        (preset) =>
          preset.range[0] === this.pickerStart &&
          preset.range[1] === this.pickerEnd
      );
    },
    mergedActionLabels() {
      return { ...DEFAULT_ACTION_LABELS, ...this.actionLabels };
    },
  },
  methods: {
    /**
     * Emit the input event with the updated range data.
     * This makes v-model work.
     */
    applyRange() {
      this.menu = false;
      this.emitRange();
    },

    applyCustomRange() {
      this.menu = false;
      // this.pickerStart = moment(this.dateStart, ISO_FORMAT).format("YYYY-MM-DD")
      // this.pickerEnd = moment(this.dateEnd, ISO_FORMAT).format("YYYY-MM-DD")
      this.$emit("input", {
        start: this.dateStart,
        end: this.dateEnd,
      });
    },
    /**
     * Called every time the menu is closed.
     * It also emits an event to tell the parent
     * that the menu has closed.
     *
     * Upon closing the datepicker values are set
     * to the current selected value.
     */
    closeMenu() {
      // Reset the changed values for datepicker models.
      this.pickerStart = this.value.start;
      this.pickerEnd = this.value.end;
      this.highlight();
      this.$emit("menu-closed");
      setTimeout(() => {
        this.showPicker = false;
      }, 250);
    },
    
    highlight() {
      if (!this.bothSelected) {
        return;
      }
      const dates = [];
      const classes = {};

      const start = moment(this.pickerStart, 'YYYY-MM-DD');
      const end = moment(this.pickerEnd, 'YYYY-MM-DD');   

      const diff = Math.abs(parseInt(moment(start, 'YYYY-MM-DD').diff(moment(end, 'YYYY-MM-DD'), 'days')));

      // Loop though all the days in range.
      for (let i = 0; i <= diff; i++) {        
        const date = moment(start, 'YYYY-MM-DD').add(i, 'day');
        dates.push(date);
        const classesArr = [];
        classesArr.push(`v-date-range__in-range`);
        classesArr.push(this.highlightColor);
        i === 0 && classesArr.push(`v-date-range__range-start`);
        i === diff && classesArr.push(`v-date-range__range-end`);
        classes[date] = classesArr.join(" ");
      }
      this.highlightDates = dates;
      this.highlightClasses = classes;
    },
    selectPreset(presetIndex) {
      this.pickerStart = this.presets[presetIndex].range[0];
      this.pickerEnd = this.presets[presetIndex].range[1];
      if (this.presets[presetIndex].showPicker) {
        this.showPicker = true;
      }

      if (this.presets[presetIndex].autoApply) {
        this.applyRange();
      }
    },
    reset() {
      // Reset Picker Values
      this.pickerStart = "";
      this.pickerEnd = "";
      this.highlightDates = [];
      this.highlightClasses = {};
      this.emitRange();
    },
    emitRange() {
      this.$emit("input", {
        start: this.pickerStart ? moment(this.pickerStart, 'YYYY-MM-DD').format('YYYY-MM-DD') : '',
        end: this.pickerEnd ? moment(this.pickerEnd, 'YYYY-MM-DD').format('YYYY-MM-DD') : '',
      });
    },
  },
  watch: {
    // Watching to see if the menu is closed.
    menu(isOpen) {
      if (!isOpen) {
        this.closeMenu();
      } else {
        this.highlight();
      }
    },
    pickerStart: "highlight",
    pickerEnd: "highlight",
  },
};
</script>
